/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  Customer,
  CustomerAddress,
  CustomerEmailSent,
  DeleteAdminV1StoresStoreIdCustomersIdAddressesAddressIdPathParameters,
  DeleteAdminV1StoresStoreIdCustomersIdPathParameters,
  GetAdminV1StoresStoreIdCustomers200,
  GetAdminV1StoresStoreIdCustomersIdAddresses200,
  GetAdminV1StoresStoreIdCustomersIdAddressesPathParameters,
  GetAdminV1StoresStoreIdCustomersIdPathParameters,
  GetAdminV1StoresStoreIdCustomersIdTimeline200,
  GetAdminV1StoresStoreIdCustomersIdTimelineParams,
  GetAdminV1StoresStoreIdCustomersIdTimelinePathParameters,
  GetAdminV1StoresStoreIdCustomersParams,
  GetAdminV1StoresStoreIdCustomersPathParameters,
  GetAdminV1StoresStoreIdNewsletterSubscribers200,
  GetAdminV1StoresStoreIdNewsletterSubscribersParams,
  GetAdminV1StoresStoreIdNewsletterSubscribersPathParameters,
  GetAdminV1StoresStoreIdPrivacyRequests200,
  GetAdminV1StoresStoreIdPrivacyRequestsIdExportPathParameters,
  GetAdminV1StoresStoreIdPrivacyRequestsIdPathParameters,
  GetAdminV1StoresStoreIdPrivacyRequestsParams,
  GetAdminV1StoresStoreIdPrivacyRequestsPathParameters,
  PatchAdminV1StoresStoreIdCustomersIdAddressesAddressIdBody,
  PatchAdminV1StoresStoreIdCustomersIdAddressesAddressIdPathParameters,
  PatchAdminV1StoresStoreIdCustomersIdBody,
  PatchAdminV1StoresStoreIdCustomersIdPathParameters,
  PatchAdminV1StoresStoreIdPrivacyRequestsIdBody,
  PatchAdminV1StoresStoreIdPrivacyRequestsIdPathParameters,
  PostAdminV1StoresStoreIdCustomersBody,
  PostAdminV1StoresStoreIdCustomersIdAddressesBody,
  PostAdminV1StoresStoreIdCustomersIdAddressesPathParameters,
  PostAdminV1StoresStoreIdCustomersIdInviteBody,
  PostAdminV1StoresStoreIdCustomersIdInvitePathParameters,
  PostAdminV1StoresStoreIdCustomersIdMergeBody,
  PostAdminV1StoresStoreIdCustomersIdMergePathParameters,
  PostAdminV1StoresStoreIdCustomersIdPasswordResetEmailBody,
  PostAdminV1StoresStoreIdCustomersIdPasswordResetEmailPathParameters,
  PostAdminV1StoresStoreIdCustomersIdPrivacyRequestsBody,
  PostAdminV1StoresStoreIdCustomersIdPrivacyRequestsPathParameters,
  PostAdminV1StoresStoreIdCustomersPathParameters,
  PrivacyExportBundle,
  PrivacyRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Add an address for a customer; the first one becomes the default
 */
export const postAdminV1StoresStoreIdCustomersIdAddresses = (
    { storeId, id }: PostAdminV1StoresStoreIdCustomersIdAddressesPathParameters,
    postAdminV1StoresStoreIdCustomersIdAddressesBody: BodyType<PostAdminV1StoresStoreIdCustomersIdAddressesBody>,
 options?: SecondParameter<typeof customInstance<CustomerAddress>>,) => {
      return customInstance<CustomerAddress>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}/addresses`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdCustomersIdAddressesBody
    },
      options);
    }
  /**
 * @summary Saved addresses of a customer, default shipping first
 */
export const getAdminV1StoresStoreIdCustomersIdAddresses = (
    { storeId, id }: GetAdminV1StoresStoreIdCustomersIdAddressesPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdCustomersIdAddresses200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdCustomersIdAddresses200>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}/addresses`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Edit a customer's address or make it a default
 */
export const patchAdminV1StoresStoreIdCustomersIdAddressesAddressId = (
    { storeId, id, addressId }: PatchAdminV1StoresStoreIdCustomersIdAddressesAddressIdPathParameters,
    patchAdminV1StoresStoreIdCustomersIdAddressesAddressIdBody: BodyType<PatchAdminV1StoresStoreIdCustomersIdAddressesAddressIdBody>,
 options?: SecondParameter<typeof customInstance<CustomerAddress>>,) => {
      return customInstance<CustomerAddress>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}/addresses/${addressId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdCustomersIdAddressesAddressIdBody
    },
      options);
    }
  /**
 * @summary Delete a customer's address
 */
export const deleteAdminV1StoresStoreIdCustomersIdAddressesAddressId = (
    { storeId, id, addressId }: DeleteAdminV1StoresStoreIdCustomersIdAddressesAddressIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}/addresses/${addressId}`, method: 'DELETE'
    },
      options);
    }
  /**
 * For a request made by phone or email. Completed like any other at /privacy-requests/{id}. One open request per kind (409 ALREADY_EXISTS)
 * @summary Open an export or deletion request on the customer's behalf
 */
export const postAdminV1StoresStoreIdCustomersIdPrivacyRequests = (
    { storeId, id }: PostAdminV1StoresStoreIdCustomersIdPrivacyRequestsPathParameters,
    postAdminV1StoresStoreIdCustomersIdPrivacyRequestsBody: BodyType<PostAdminV1StoresStoreIdCustomersIdPrivacyRequestsBody>,
 options?: SecondParameter<typeof customInstance<PrivacyRequest>>,) => {
      return customInstance<PrivacyRequest>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}/privacy-requests`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdCustomersIdPrivacyRequestsBody
    },
      options);
    }
  /**
 * Runs the privacy deletion at once: the row, identities, sessions, addresses and wishlists are anonymized or removed and the orders redacted; the email is freed. Recorded as a completed delete request. 409 RESOURCE_IN_USE while the customer has open orders
 * @summary Delete a customer (anonymizing deletion)
 */
export const deleteAdminV1StoresStoreIdCustomersId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdCustomersIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Get a customer
 */
export const getAdminV1StoresStoreIdCustomersId = (
    { storeId, id }: GetAdminV1StoresStoreIdCustomersIdPathParameters,
 options?: SecondParameter<typeof customInstance<Customer>>,) => {
      return customInstance<Customer>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * A new email must be unique in the store (409 ALREADY_EXISTS). Re-enabling a customer who never set a password makes them a guest
 * @summary Update email, names, tags, notes, marketing consent or status
 */
export const patchAdminV1StoresStoreIdCustomersId = (
    { storeId, id }: PatchAdminV1StoresStoreIdCustomersIdPathParameters,
    patchAdminV1StoresStoreIdCustomersIdBody: BodyType<PatchAdminV1StoresStoreIdCustomersIdBody>,
 options?: SecondParameter<typeof customInstance<Customer>>,) => {
      return customInstance<Customer>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdCustomersIdBody
    },
      options);
    }
  /**
 * The link works once for 24 hours and lands on resetUrl (or the store's account/reset page); setting the password makes a guest an account. 409 for a disabled customer
 * @summary Email an account invitation with a set-password link
 */
export const postAdminV1StoresStoreIdCustomersIdInvite = (
    { storeId, id }: PostAdminV1StoresStoreIdCustomersIdInvitePathParameters,
    postAdminV1StoresStoreIdCustomersIdInviteBody: BodyType<PostAdminV1StoresStoreIdCustomersIdInviteBody>,
 options?: SecondParameter<typeof customInstance<CustomerEmailSent>>,) => {
      return customInstance<CustomerEmailSent>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}/invite`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdCustomersIdInviteBody
    },
      options);
    }
  /**
 * Same link as the storefront's forgot-password flow, valid 15 minutes, sent to the customer's email
 * @summary Email a password reset link
 */
export const postAdminV1StoresStoreIdCustomersIdPasswordResetEmail = (
    { storeId, id }: PostAdminV1StoresStoreIdCustomersIdPasswordResetEmailPathParameters,
    postAdminV1StoresStoreIdCustomersIdPasswordResetEmailBody: BodyType<PostAdminV1StoresStoreIdCustomersIdPasswordResetEmailBody>,
 options?: SecondParameter<typeof customInstance<CustomerEmailSent>>,) => {
      return customInstance<CustomerEmailSent>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}/password-reset-email`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdCustomersIdPasswordResetEmailBody
    },
      options);
    }
  /**
 * Customer changes (created, updated, merged, invited...), the events of their orders (placed, paid, shipped, refunded...) and their privacy requests, merged into one list
 * @summary What happened to the customer, newest first
 */
export const getAdminV1StoresStoreIdCustomersIdTimeline = (
    { storeId, id }: GetAdminV1StoresStoreIdCustomersIdTimelinePathParameters,
    params?: GetAdminV1StoresStoreIdCustomersIdTimelineParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdCustomersIdTimeline200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdCustomersIdTimeline200>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}/timeline`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Orders, drafts, addresses, wishlist items, gift cards, discount uses, uploads, tags and notes of sourceCustomerId move onto this customer, whose profile wins; counters are recounted. The duplicate is hidden and its email freed
 * @summary Merge a duplicate into this customer
 */
export const postAdminV1StoresStoreIdCustomersIdMerge = (
    { storeId, id }: PostAdminV1StoresStoreIdCustomersIdMergePathParameters,
    postAdminV1StoresStoreIdCustomersIdMergeBody: BodyType<PostAdminV1StoresStoreIdCustomersIdMergeBody>,
 options?: SecondParameter<typeof customInstance<Customer>>,) => {
      return customInstance<Customer>(
      {url: `/admin/v1/stores/${storeId}/customers/${id}/merge`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdCustomersIdMergeBody
    },
      options);
    }
  /**
 * @summary Customers of the store, newest first
 */
export const getAdminV1StoresStoreIdCustomers = (
    { storeId }: GetAdminV1StoresStoreIdCustomersPathParameters,
    params?: GetAdminV1StoresStoreIdCustomersParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdCustomers200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdCustomers200>(
      {url: `/admin/v1/stores/${storeId}/customers`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Create a customer record (guest until they set a password)
 */
export const postAdminV1StoresStoreIdCustomers = (
    { storeId }: PostAdminV1StoresStoreIdCustomersPathParameters,
    postAdminV1StoresStoreIdCustomersBody: BodyType<PostAdminV1StoresStoreIdCustomersBody>,
 options?: SecondParameter<typeof customInstance<Customer>>,) => {
      return customInstance<Customer>(
      {url: `/admin/v1/stores/${storeId}/customers`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdCustomersBody
    },
      options);
    }
  /**
 * @summary Privacy requests of the store, newest first
 */
export const getAdminV1StoresStoreIdPrivacyRequests = (
    { storeId }: GetAdminV1StoresStoreIdPrivacyRequestsPathParameters,
    params?: GetAdminV1StoresStoreIdPrivacyRequestsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdPrivacyRequests200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdPrivacyRequests200>(
      {url: `/admin/v1/stores/${storeId}/privacy-requests`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary One privacy request
 */
export const getAdminV1StoresStoreIdPrivacyRequestsId = (
    { storeId, id }: GetAdminV1StoresStoreIdPrivacyRequestsIdPathParameters,
 options?: SecondParameter<typeof customInstance<PrivacyRequest>>,) => {
      return customInstance<PrivacyRequest>(
      {url: `/admin/v1/stores/${storeId}/privacy-requests/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * received → in_progress | completed | rejected; in_progress → completed | rejected. Completing an export builds and stores the bundle; completing a deletion anonymizes the customer, their addresses, identities, sessions and wishlists and redacts their orders. 409 CONFLICT with meta.allowed otherwise
 * @summary Move a request forward
 */
export const patchAdminV1StoresStoreIdPrivacyRequestsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdPrivacyRequestsIdPathParameters,
    patchAdminV1StoresStoreIdPrivacyRequestsIdBody: BodyType<PatchAdminV1StoresStoreIdPrivacyRequestsIdBody>,
 options?: SecondParameter<typeof customInstance<PrivacyRequest>>,) => {
      return customInstance<PrivacyRequest>(
      {url: `/admin/v1/stores/${storeId}/privacy-requests/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdPrivacyRequestsIdBody
    },
      options);
    }
  /**
 * @summary The export bundle of a completed export request
 */
export const getAdminV1StoresStoreIdPrivacyRequestsIdExport = (
    { storeId, id }: GetAdminV1StoresStoreIdPrivacyRequestsIdExportPathParameters,
 options?: SecondParameter<typeof customInstance<PrivacyExportBundle>>,) => {
      return customInstance<PrivacyExportBundle>(
      {url: `/admin/v1/stores/${storeId}/privacy-requests/${id}/export`, method: 'GET'
    },
      options);
    }
  /**
 * Filter by status (pending, subscribed, unsubscribed) and any part of the email. Export the whole list with kind newsletter_csv
 * @summary Newsletter subscribers of the store, newest first
 */
export const getAdminV1StoresStoreIdNewsletterSubscribers = (
    { storeId }: GetAdminV1StoresStoreIdNewsletterSubscribersPathParameters,
    params?: GetAdminV1StoresStoreIdNewsletterSubscribersParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdNewsletterSubscribers200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdNewsletterSubscribers200>(
      {url: `/admin/v1/stores/${storeId}/newsletter/subscribers`, method: 'GET',
        params
    },
      options);
    }
  export type PostAdminV1StoresStoreIdCustomersIdAddressesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdCustomersIdAddresses>>>
export type GetAdminV1StoresStoreIdCustomersIdAddressesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdCustomersIdAddresses>>>
export type PatchAdminV1StoresStoreIdCustomersIdAddressesAddressIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdCustomersIdAddressesAddressId>>>
export type DeleteAdminV1StoresStoreIdCustomersIdAddressesAddressIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdCustomersIdAddressesAddressId>>>
export type PostAdminV1StoresStoreIdCustomersIdPrivacyRequestsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdCustomersIdPrivacyRequests>>>
export type DeleteAdminV1StoresStoreIdCustomersIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdCustomersId>>>
export type GetAdminV1StoresStoreIdCustomersIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdCustomersId>>>
export type PatchAdminV1StoresStoreIdCustomersIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdCustomersId>>>
export type PostAdminV1StoresStoreIdCustomersIdInviteResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdCustomersIdInvite>>>
export type PostAdminV1StoresStoreIdCustomersIdPasswordResetEmailResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdCustomersIdPasswordResetEmail>>>
export type GetAdminV1StoresStoreIdCustomersIdTimelineResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdCustomersIdTimeline>>>
export type PostAdminV1StoresStoreIdCustomersIdMergeResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdCustomersIdMerge>>>
export type GetAdminV1StoresStoreIdCustomersResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdCustomers>>>
export type PostAdminV1StoresStoreIdCustomersResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdCustomers>>>
export type GetAdminV1StoresStoreIdPrivacyRequestsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdPrivacyRequests>>>
export type GetAdminV1StoresStoreIdPrivacyRequestsIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdPrivacyRequestsId>>>
export type PatchAdminV1StoresStoreIdPrivacyRequestsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdPrivacyRequestsId>>>
export type GetAdminV1StoresStoreIdPrivacyRequestsIdExportResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdPrivacyRequestsIdExport>>>
export type GetAdminV1StoresStoreIdNewsletterSubscribersResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdNewsletterSubscribers>>>
